"use client";


import type {
  AdyenCheckoutError,
  PaymentCompletedData,
  PaymentFailedData,
  SessionsResponse,
} from "@adyen/adyen-web";
import { useMarket } from "@context/marketContext";
import type { AdyenCheckoutForm } from "@server/gql/graphql";
import { useRouter } from "next/navigation";
import { useCallback } from "react";

import type { AdyenDoneCookiePayloadType } from "@server/utils/cart";
import { setAdyenDoneCookie } from "@server/utils/cart";
import { useCheckoutContext } from "../../Checkout.hooks";
import { useAdyenCheckoutInstance, useAdyenWidgetMount } from "./Adyen.hooks";
import { adyenStore } from "./Adyen.store";
import { getAdyenPaymentCompletedReturnUrl } from "./Adyen.utils";
import AdyenErrorMessage from "./AdyenErrorMessage";
import AdyenWidgetError from "./AdyenWidgetError";
import AdyenWidgetLoadingSkeleton from "./AdyenWidgetLoadingSkeleton";
import AdyenWidgetRef from "./AdyenWidgetRef";
import AdyenWidgetReloadButton from "./AdyenWidgetReloadButton";

export default function AdyenWidget({
  adyenCheckoutForm,
  cartId,
}: {
  adyenCheckoutForm: AdyenCheckoutForm;
  cartId: number;
}) {
  const { locale } = useMarket().state.market;
  const router = useRouter();
  const { adyenDropInRef } = useCheckoutContext();
  const setAdyenDropInError = adyenStore((state) => state.setAdyenDropInError);

  const handlePaymentCompleted = useCallback(
    (paymentCompletedData: PaymentCompletedData) => {
      console.info("AdyenWidget.onPaymentCompleted", paymentCompletedData);

      const asSessionsResponse = paymentCompletedData as SessionsResponse;

      const toStore: AdyenDoneCookiePayloadType = {
        sessionId: adyenCheckoutForm.id,
        sessionData: asSessionsResponse.sessionData,
        sessionResult: asSessionsResponse.sessionResult,
        cartId: cartId.toString(),
      };

      setAdyenDoneCookie(toStore);

      const paymentCompletedReturnUrl = getAdyenPaymentCompletedReturnUrl({
        locale,
        cartId: toStore.cartId,
        paymentCompletedData,
        adyenCheckoutForm,
      });

      console.info(
        "AdyenWidget.onPaymentCompleted - Redirecting to",
        paymentCompletedReturnUrl,
      );

      setTimeout(() => {
        router.push(paymentCompletedReturnUrl);
      }, 1000);
    },
    [locale, adyenCheckoutForm, router, cartId],
  );

  const handlePaymentFailed = useCallback(
    (paymentFailedData: PaymentFailedData) => {
      console.error("AdyenWidget.onPaymentFailed", paymentFailedData);

      setAdyenDropInError(paymentFailedData);
    },
    [setAdyenDropInError],
  );

  const handleError = useCallback(
    (error: AdyenCheckoutError) => {
      console.error("AdyenWidget.onError", error);

      setAdyenDropInError(error);
    },
    [setAdyenDropInError],
  );

  const { data, error, isPending, isSuccess, isError } =
    useAdyenCheckoutInstance(
      {
        id: adyenCheckoutForm.id,
        sessionData: adyenCheckoutForm.sessionData ?? undefined,
      },
      {
        onPaymentCompleted: handlePaymentCompleted,
        onPaymentFailed: handlePaymentFailed,
        onError: handleError,
      },
    );

  useAdyenWidgetMount({
    ref: adyenDropInRef,
    data,
  });

  return (
    <div className="flex flex-col gap-8">
      {!isError && (
        <>
          <AdyenWidgetError />
          <AdyenWidgetReloadButton />
        </>
      )}
      {error && <AdyenErrorMessage message={error.message} />}
      {isPending && <AdyenWidgetLoadingSkeleton />}
      {isSuccess && <AdyenWidgetRef ref={adyenDropInRef} />}
    </div>
  );
}
